import { createContext, useContext, type ReactElement, type ReactNode } from 'react';
import { useRouterState, type AnyRouter } from '@tanstack/react-router';
import { PageLoader } from '@/components/ui/page-loader';
import { Skeleton, SkeletonListRows, SkeletonStatus } from '@/components/ui/skeleton';

/** True below an app shell's content outlet (salon or agency): a loading page keeps the rail and shows a skeleton. */
const InShellContext = createContext(false);

/** Marks the shell's page outlet, so pages loading inside it show a skeleton instead of the full-screen boot mark. */
export function InShellRoutePending({ children }: { children: ReactNode }) {
  return <InShellContext.Provider value>{children}</InShellContext.Provider>;
}

/**
 * The router's pending UI while a page's code chunk (or a `beforeLoad`) is loading. Before any shell is drawn it is
 * the boot `PageLoader` (same mark as the HTML boot splash, so the hand-off doesn't blink); inside a shell it is a
 * neutral page skeleton — the page's own skeleton takes over as soon as its code arrives.
 */
export function RoutePendingFallback() {
  const inShell = useContext(InShellContext);
  if (!inShell) return <PageLoader />;
  return (
    <SkeletonStatus className="p-4 md:p-6">
      <Skeleton className="h-8 w-48" />
      <SkeletonListRows rows={6} className="mt-6" rowClassName="py-2" />
    </SkeletonStatus>
  );
}

/**
 * Until the first navigation commits there are no matches to render (not even a pending one), so the screen would go
 * blank between Clerk's loader and the route's pending loader. Keep the boot mark up for that window.
 */
export function BootLoaderUntilFirstMatch({ children }: { children: ReactNode }): ReactElement {
  // `AnyRouter`: the registered router type is built from this file's export (router.tsx), so it can't be used here.
  const hasMatches = useRouterState<AnyRouter, boolean>({ select: (state) => state.matches.length > 0 });
  return (
    <>
      {children}
      {!hasMatches && <PageLoader />}
    </>
  );
}
